import type { KeyboardEvent, PointerEvent } from 'react';
import { Draggable } from '@carbon/react/icons';
import { TOOLS_WIDTH } from './utils';
import styles from './area-geometry-editor.module.scss';

interface EditorSplitterProps {
  width: number;
  onWidthChange: (width: number) => void;
  onReset: () => void;
  /** Reports a drag starting and ending, so the row can hold its cursor. */
  onResizingChange: (resizing: boolean) => void;
}

/**
 * The bar between map and tools: drag it, or use the arrow keys (Shift for
 * bigger steps), to trade width; Home or a double-click puts it back.
 *
 * @param {EditorSplitterProps} props - The tools width and its setters.
 * @return {JSX.Element} The splitter.
 */
export function EditorSplitter({
  width,
  onWidthChange,
  onReset,
  onResizingChange,
}: EditorSplitterProps): JSX.Element {
  /**
   * Starts a drag: the pointer is captured, so the drag follows it off the bar.
   *
   * @param {PointerEvent<HTMLDivElement>} event - The press on the bar.
   */
  const handlePointerDown = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);
    const startX = event.clientX;
    const startWidth = width;
    onResizingChange(true);
    // The tools sit right of the bar, so moving left widens them.
    /**
     * Follows the pointer.
     *
     * @param {globalThis.PointerEvent} move - The pointer's move.
     */
    const handleMove = (move: globalThis.PointerEvent) =>
      onWidthChange(startWidth - (move.clientX - startX));
    /** Ends the drag. */
    const handleUp = () => {
      handle.removeEventListener('pointermove', handleMove);
      handle.removeEventListener('pointerup', handleUp);
      handle.removeEventListener('pointercancel', handleUp);
      onResizingChange(false);
    };
    handle.addEventListener('pointermove', handleMove);
    handle.addEventListener('pointerup', handleUp);
    handle.addEventListener('pointercancel', handleUp);
  };

  /**
   * Arrow keys move the bar, Shift for bigger steps; Home puts it back.
   *
   * @param {KeyboardEvent<HTMLDivElement>} event - The key press.
   */
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const step = event.shiftKey ? 64 : 16;
    if (event.key === 'Home') {
      event.preventDefault();
      onReset();
      return;
    }
    if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
    event.preventDefault();
    onWidthChange(width + (event.key === 'ArrowLeft' ? step : -step));
  };

  /* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex -- a focusable separator with a value is ARIA's window-splitter widget, which is interactive */
  return (
    <div
      className={styles.splitter}
      role="separator"
      aria-orientation="vertical"
      aria-label="Resize map and tools"
      aria-valuenow={width}
      aria-valuemin={TOOLS_WIDTH.min}
      tabIndex={0}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      onDoubleClick={onReset}
    >
      <span className={styles.splitterGrip} aria-hidden="true">
        <Draggable size={16} />
      </span>
    </div>
  );
  /* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex -- the splitter ends here */
}
